import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { isArray } from 'ionic-angular/util/util';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { isFileChangeQItem } from '../../../core/services/sync-up/file-item';
import { isRestfulAction, SynchronisableAction } from '../../../core/store/actions/base-actions';
import { systemEventTypes } from '../../../core/system/system.actions';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import * as actions from './sync-up-item.actions';

@Injectable()
export class SyncUpItemSideEffects {

    @Effect({dispatch: false})
    clearErrors$ = this.actions$
        .ofType(actions.CLEAR_ERRORS)
        .switchMap(() => {
            return this.appFacade.objects.syncUpItem.store
                .getAll()
                .take(1)
                .map(syncUpItems => syncUpItems
                    .filter(item => item.hasResponse && !item.ok)
                    .forEach(syncUpItem =>
                        this.appFacade.objects.syncUpItem.store.remove(
                            syncUpItem
                        )
                    )
                )
                .catch(error => this.appFacade.alert.ok('Error', error));
        });

    @Effect({dispatch: false})
    $create = this.actions$
        .ofType(actions.CREATE)
        .do((action: actions.CreateAction) => {
            this.appFacade.changeLog.enqueueItem(action.payload.id, action.payload.changeQItem);
        });

    @Effect({dispatch: false})
    $update = this.actions$
        .ofType(actions.UPDATE)
        .do((action: actions.UpdateAction) => {
            this.appFacade.changeLog.updateItem(action.payload.id, action.payload.changeQItem).then(
                () => {
                    // noop
                },
                () => {
                    // noop
                }
            );
        });

    @Effect({dispatch: false})
    $removeByKey = this.actions$
        .ofType(actions.REMOVE_BY_KEY)
        .switchMap(action => {

            this.appFacade.objects.syncUpItem.store.get(action.payload.key)
                .take(1)
                .subscribe(syncUpItem => {
                    if (!syncUpItem) {
                        return;
                    }
                    this.appFacade.objects.syncUpItem.store.remove(syncUpItem);
                });

            this.appFacade.changeLog.removeItem(action.payload.key).then(
                () => {
                    // noop
                },
                () => {
                    // noop
                }
            );

            return of(undefined);
        });

    @Effect({dispatch: false})
    $remove = this.actions$
        .ofType(actions.REMOVE)
        .switchMap((action: actions.RemoveAction) => {
            return of(action)
                .do(() => {

                    if (!isFileChangeQItem(action.payload.changeQItem)) {
                        return;
                    }

                    // this logic is important to recover memory after adding
                    // files in the file manager, since they are saved as blobs
                    // in the browser's memory. After we remove the sync up
                    // item, we revoke the url to recover the memory
                    const url = action.payload.changeQItem.localFileSystemPath;

                    if (url && url.startsWith('blob:')) {
                        URL.revokeObjectURL(url);
                    }
                })
                // this logic removes the change q item from local storage
                .concatMap(async () => {
                    if (
                        !action.payload ||
                        !action.payload.changeQItem ||
                        !action.payload.changeQItem.key
                    ) {
                        console.warn('invalid payload', action);
                        return;
                    }

                    return this.appFacade.changeLog.removeItem(
                        action.payload.changeQItem.key
                    );
                })
                .catch(error => this.appFacade.toast.toastDanger(error));
        });

    @Effect({dispatch: false})
    $retry = this.actions$
        .ofType(actions.RETRY)
        .do((action: actions.RetryAction) => {
            this.appFacade.syncUp.syncUpItem(action.payload.changeQItem).then(
                () => {
                    // noop
                },
                error => this.appFacade.toast.toastDanger(error)
            );
        });

    @Effect({dispatch: false})
    $restful = this.actions$
        .do((action: SynchronisableAction) => {

            if (!isRestfulAction(action)) {
                return;
            }

            if (action.changeQItem) {
                this.appFacade.objects.syncUpItem.store.createFromChangeQItem(action.changeQItem);
            }

            if (isArray(action.changeQItems)) {
                action.changeQItems.forEach(item => {
                    this.appFacade.objects.syncUpItem.store.createFromChangeQItem(item);
                });
            }
        });

    @Effect()
    syncUp$ = this.actions$
        .ofType(
            systemEventTypes.SyncUpStartEvent,
            systemEventTypes.SyncUpEndEvent,
            actions.CLEAR_ERRORS,
            actions.CLEAR,
            actions.CREATE
        )
        .throttleTime(500)
        .switchMap(() => {
            return fromPromise(this.appFacade.changeLog.getCount())
                .map(count => new actions.SetSyncCountAction({count}));
        });

    constructor(
        protected actions$: Actions,
        protected appFacade: VasAppFacade
    ) {

    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/sync-up-item/sync-up-item.side-effects.ts